import React, { createContext, useEffect, useState, useRef, useCallback } from "react";
import { jwtDecode } from "jwt-decode";
import { setupAxiosInterceptors } from "../utils/axiosConfig";
import { logoutUser, refreshAccessToken, getMe } from "../services/authService";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [token, setToken] = useState(localStorage.getItem("token"));
    const [role, setRole] = useState(null);
    const [user, setUser] = useState(null); // User profile: { id, firstName, lastName, email, role }
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loading, setLoading] = useState(true);
    const interceptorsSetup = useRef(false);
    const tokenRef = useRef(token);

    // Keep tokenRef in sync with token state
    useEffect(() => {
        tokenRef.current = token;
    }, [token]);

    // Clear local state without calling backend (used when token is invalid/expired)
    const clearLocalState = useCallback(() => {
        localStorage.removeItem("token");
        setToken(null);
        tokenRef.current = null;
        setRole(null);
        setUser(null); // Clear user profile
        setIsAuthenticated(false);
    }, []);

    // Full logout - calls backend to invalidate refresh token
    const logout = useCallback(async () => {
        // Only call backend if we have a token (user was actually logged in)
        const currentToken = tokenRef.current;
        if (currentToken) {
            try {
                await logoutUser();
            } catch (error) {
                // Even if backend call fails, we still clear frontend state
                // (e.g., if backend is down, user should still be logged out locally)
                console.error("Logout API call failed:", error);
            }
        }
        
        // Clear frontend state
        clearLocalState();
    }, [clearLocalState]);

    // Setup axios interceptors once
    useEffect(() => {
        if (!interceptorsSetup.current) {
            setupAxiosInterceptors(
                () => tokenRef.current, // getToken function - reads from ref
                async (newToken) => {
                    // setToken function - updates both state and localStorage
                    localStorage.setItem("token", newToken);
                    setToken(newToken);
                    tokenRef.current = newToken;
                    try {
                        const decoded = jwtDecode(newToken);
                        setRole(decoded.role);
                        
                        // Fetch user profile after token refresh
                        // getMe() uses axios interceptor to attach token automatically
                        try {
                            const userProfile = await getMe();
                            setUser(userProfile);
                        } catch (profileError) {
                            console.error("Error fetching user profile after token refresh:", profileError);
                            // Don't fail the token refresh if profile fetch fails
                        }
                    } catch (err) {
                        console.error("Error decoding new token:", err);
                    }
                },
                logout // logout function
            );
            interceptorsSetup.current = true;
        }
    }, [logout]);

    useEffect(() => {
        const checkToken = async () => {
            const storedToken = localStorage.getItem("token");
            if (storedToken) {
                try {
                    const decoded = jwtDecode(storedToken);
                    const now = Date.now() / 1000;
                    if (decoded.exp && decoded.exp > now) {
                        // Token is valid
                        setToken(storedToken);
                        tokenRef.current = storedToken;
                        setRole(decoded.role);
                        setIsAuthenticated(true);
                        
                        // Fetch user profile from backend
                        // getMe() uses axios interceptor to attach token automatically
                        try {
                            const userProfile = await getMe();
                            setUser(userProfile);
                        } catch (profileError) {
                            console.error("Error fetching user profile on app load:", profileError);
                            // Continue even if profile fetch fails - user is still authenticated
                        }
                        
                        setLoading(false);
                    } else {
                        // Token expired - try to refresh it using refresh token cookie
                        // Only attempt refresh if we had a token (user was logged in before)
                        try {
                            const response = await refreshAccessToken();
                            if (response.success && (response.accessToken || response.token)) {
                                const newToken = response.accessToken || response.token;
                                localStorage.setItem("token", newToken);
                                setToken(newToken);
                                tokenRef.current = newToken;
                                try {
                                    const decoded = jwtDecode(newToken);
                                    setRole(decoded.role);
                                    setIsAuthenticated(true);
                                    
                                    // Fetch user profile after successful token refresh
                                    // getMe() uses axios interceptor to attach token automatically
                                    try {
                                        const userProfile = await getMe();
                                        setUser(userProfile);
                                    } catch (profileError) {
                                        console.error("Error fetching user profile after refresh:", profileError);
                                        // Don't fail the refresh if profile fetch fails
                                    }
                                    
                                    // Token refreshed successfully - user stays logged in
                                } catch (err) {
                                    console.error("Error decoding refreshed token:", err);
                                    clearLocalState();
                                }
                            } else {
                                // Refresh failed - no valid refresh token cookie
                                // This is expected if refresh token expired or user logged out
                                clearLocalState();
                            }
                        } catch (refreshError) {
                            // Unexpected error during refresh (network error, etc.)
                            // Only log if it's not a 401 (401 is expected when no refresh token)
                            if (refreshError.response?.status !== 401) {
                                console.error("Unexpected error during token refresh:", refreshError);
                            }
                            clearLocalState();
                        }
                        setLoading(false);
                    }
                } catch (err) {
                    // Invalid token format - clear state
                    console.error("Invalid token format:", err);
                    clearLocalState();
                    setLoading(false);
                }
            } else {
                // No token at all - user never logged in or already cleared
                // Don't attempt refresh - just clear state
                clearLocalState();
                setLoading(false);
            }
        };

        checkToken();
    }, [clearLocalState]);

    /**
     * Login function - stores token and fetches user profile
     * @param {string} accessToken - JWT access token from backend
     */
    const login = async (accessToken) => {
        // Store only access token in localStorage
        // Refresh token is stored in HTTP-only cookie by backend (not accessible via JS)
        localStorage.setItem("token", accessToken);
        setToken(accessToken);
        tokenRef.current = accessToken;
        
        // Decode JWT for quick role-based routing (not for security)
        const decoded = jwtDecode(accessToken);
        setRole(decoded.role);
        setIsAuthenticated(true);
        
        // Fetch user profile from backend (single source of truth)
        // getMe() uses axios interceptor to attach token automatically
        try {
            const userProfile = await getMe();
            setUser(userProfile);
        } catch (profileError) {
            console.error("Error fetching user profile after login:", profileError);
            // Continue even if profile fetch fails - user is still authenticated
            // Role from JWT is still available for routing
        }
    };

    return (
        <AuthContext.Provider value={{ token, role, user, isAuthenticated, login, logout, loading }}>
            {children}
        </AuthContext.Provider>
    );
};

